import { useEffect, useMemo, useState } from "react"
import { MdLock, MdPages, MdWarningAmber } from "react-icons/md"
import Modal from "../../components/Modal"
import Button from "../../components/Form/Button"
import Checkbox from "../../components/Form/Checkbox"
import Input from "../../components/Form/Input"
import ImageWithFallback from "../../components/ImageWithFallback"
import Loading from "../layouts/Loading"
import api from "../../services/api"
import styles from "./metaIntegration.module.css"

const normalizePageId = value => String(value || "").replace(/\D/g, "")

const ModalMetaPageSelection = ({ connection, closeModal, onSaved }) => {
    const [pages, setPages] = useState([])
    const [selected, setSelected] = useState(new Set())
    const [search, setSearch] = useState("")
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        api.post("/integrations/meta/pages/discover", { meta_user_account_id: connection._id })
            .then(response => {
                const data = response?.data?.data || {}
                setPages(data.pages || [])
                setSelected(new Set((data.selected_page_ids || []).map(normalizePageId).filter(Boolean)))
            })
            .catch(err => setError(err?.response?.data?.message || "Não foi possível consultar as páginas na Meta."))
            .finally(() => setLoading(false))
    }, [connection._id])

    const filtered = useMemo(() => {
        const term = search.trim().toLocaleLowerCase("pt-BR")
        return pages.filter(page => !term || `${page.name || ""} ${page.id || ""}`.toLocaleLowerCase("pt-BR").includes(term))
    }, [pages, search])

    const toggle = page => {
        if (page.connected || page.connected_elsewhere) return
        const id = normalizePageId(page.id)
        setSelected(previous => {
            const next = new Set(previous)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const selectFiltered = () => setSelected(previous => {
        const next = new Set(previous)
        filtered.filter(page => !page.connected_elsewhere).forEach(page => next.add(normalizePageId(page.id)))
        return next
    })

    const clearFiltered = () => setSelected(previous => {
        const next = new Set(previous)
        filtered.filter(page => !page.connected).forEach(page => next.delete(normalizePageId(page.id)))
        return next
    })

    const save = async () => {
        setSaving(true)
        setError("")
        try {
            await api.patch("/integrations/meta/pages/selection", { meta_user_account_id: connection._id, page_ids: [...selected] })
            await onSaved?.()
            closeModal()
        } catch (err) {
            setError(err?.response?.data?.message || "Não foi possível salvar as páginas selecionadas.")
        } finally { setSaving(false) }
    }

    return <Modal icon={<MdPages />} title="Páginas autorizadas" description={`Escolha as páginas disponíveis pela credencial ${connection.name || "Meta"}.`} closeModal={saving ? () => {} : closeModal} closeOnEsc={!saving} bodyClassName={styles.assetSelectionBody}>
        {loading ? <div className={styles.selectionLoading}><Loading /><p>Consultando as páginas diretamente na Meta…</p></div> : <>
            <div className={styles.selectionNotice}><MdWarningAmber /><span>Páginas selecionadas ficam prontas para conexão. Uma página já conectada permanece bloqueada até ser desconectada.</span></div>
            <div className={styles.selectionToolbar}>
                <Input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Pesquisar por nome ou ID" aria-label="Pesquisar páginas Meta" />
                <span><strong>{selected.size}</strong> de {pages.filter(page => !page.connected_elsewhere).length} selecionadas</span>
            </div>
            <div className={styles.selectionQuickActions}><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={selectFiltered}>Selecionar resultados</Button><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={clearFiltered}>Limpar resultados</Button></div>
            {error && <div className={styles.selectionError}>{error}</div>}
            <div className={styles.selectionList}>
                {filtered.map(page => {
                    const id = normalizePageId(page.id)
                    const locked = page.connected || page.connected_elsewhere
                    return <button type="button" key={id} className={`${styles.pageSelectionRow} ${locked ? styles.lockedPageSelection : ""}`} disabled={locked} aria-pressed={selected.has(id)} onClick={() => toggle(page)}>
                        <Checkbox checked={selected.has(id)} />
                        <span className={styles.pageSelectionImage}>{page.pictureUrl ? <ImageWithFallback src={page.pictureUrl} alt="" functionError={() => <MdPages />} /> : <MdPages />}</span>
                        <span className={styles.pageSelectionIdentity}><strong>{page.name || "Página Meta"}</strong><small>ID {id}</small></span>
                        <em>{page.connected ? <><MdLock /> Conectada</> : page.connected_elsewhere ? <><MdLock /> Outra conexão</> : selected.has(id) ? "Pronta para conectar" : "Disponível"}</em>
                    </button>
                })}
                {!filtered.length && <p className={styles.emptySelection}>Nenhuma página encontrada.</p>}
            </div>
            <div className={styles.selectionFooter}><Button type="button" variant="secondary" fullWidth={false} onClick={closeModal}>Cancelar</Button><Button type="button" fullWidth={false} onClick={save}>Salvar {selected.size} página(s)</Button></div>
        </>}
        {saving && <Loading fullPage={true} />}
    </Modal>
}

export default ModalMetaPageSelection
